/**
 * Direction AC (design refresh, September 2026): pearl ground, graphite type, one restrained
 * deep-blue accent, with light and dark authored as peers. These are the values the server and
 * the client both need outside the stylesheet: the grounds a tenant accent must stay readable on,
 * the default accents, and the colours server-rendered pages and mail paint with.
 *
 * The stylesheet (`src/client/styles/tokens.css`) carries the full token set; keep the two in step.
 */
export type Scheme = 'light' | 'dark';

/** The page ground: what `theme-color`, the manifest and the tenant contrast check use. */
export const GROUND: Record<Scheme, string> = { light: '#F1F2F4', dark: '#0C0D0F' };

/** The raised ground: cards, headers, the status bar. */
export const SURFACE: Record<Scheme, string> = { light: '#FFFFFF', dark: '#131417' };

/**
 * The darkest ground accent text may sit on in each scheme: the accent tint behind an in-progress
 * row (light) and the raised chip behind a selected control (dark). Recheck when those change.
 */
export const TEXT_GROUND: Record<Scheme, string> = { light: '#E6ECF7', dark: '#24262B' };

/** The endorsed default accent. Operators may replace it; the server keeps it readable. */
export const DEFAULT_ACCENT: Record<Scheme, string> = { light: '#1F3D73', dark: '#93B4EA' };

/**
 * The Horizon mark's one colour on each ground (`assets/brand/README.md` in the project folder). It is
 * the Academy's mark, so an operator's accent never replaces it.
 */
export const MARK: Record<Scheme, string> = { light: '#1F3D73', dark: '#93B4EA' };

export const INK: Record<Scheme, string> = { light: '#121316', dark: '#E9E9E7' };
export const INK_MUTED: Record<Scheme, string> = { light: '#3D4045', dark: '#C9CBCF' };
export const RULE: Record<Scheme, string> = { light: '#DDDFE3', dark: '#24262B' };

/** The typeface stack for server-rendered pages and HTML mail, which cannot load the web font. */
export const FONT_STACK =
  "'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
